import React, { useState } from "react";
import { usePreventScroll } from "../utils/use-prevent-scroll";
import { ShareButton } from "./ShareButton";
import Image from "next/image";
import styled from "@emotion/styled";
import { Spinner } from "../Spinner";
import { DialogModal } from "../DialogModal";
import { StatusTag } from "../StatusTag";

const Content = styled.div`
  display: flex;
  flex-direction: column;
  padding: 24px;
  position: relative;
  max-width: 100%;
  overflow-wrap: break-word;

  @media ${({ theme }) => theme.bp.tabletUp} {
    width: 50%;
    overflow-y: auto;
    padding: 28px 24px 24px 32px;
  }
`;

const Controls = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 32px 20px;
  position: sticky;
  top: 0;
  z-index: 2;
  background-color: ${({ theme }) => theme.colors.snowdrop};

  @media ${({ theme }) => theme.bp.tabletUp} {
    display: none;
  }
`;

const ControlsContainer = styled.div`
  min-height: 20px;
`;

const PictureAdornmentContainer = styled.div`
  position: absolute;
  top: 32px;
  left: 32px;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 16px;
`;

const FlagContainer = styled.div`
  width: 48px;
`;

const ImageLabel = styled.div`
  background: ${({ theme }) => theme.colors.snowdrop};
  bottom: 20px;
  box-shadow: 0px 2px 2px 0px rgba(0, 0, 0, 0.3);
  color: ${({ theme }) => theme.colors.night};
  font-size: 15px;
  left: 50%;
  padding: 12px 16px;
  position: absolute;
  transform: translateX(-50%);
  white-space: nowrap;

  @media ${({ theme }) => theme.bp.tabletUp} {
    bottom: 35px;
    border-radius: 10px;
  }

  // Label's up arrow
  &::after {
    position: absolute;
    top: -4px;
    left: 50%;
    width: 15px;
    height: 15px;
    background: ${({ theme }) => theme.colors.snowdrop};
    transform: translateX(-50%) rotate(45deg);
    content: "";
  }
`;

const ImageWrapper = styled.div<{ isSubProfile?: boolean }>`
  background-color: ${(props) =>
    props.isSubProfile
      ? "#107D61" // doesn't exist in theme colours
      : props.theme.colors.mushroom};
  height: 250px;
  max-width: 100%;
  flex-shrink: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  flex-grow: 1;

  @media only screen and (max-height: 500px) {
    height: 175px;
  }

  @media only screen and (max-height: 400px) {
    height: 125px;
  }

  @media ${({ theme }) => theme.bp.tabletUp} {
    height: unset;
    flex-shrink: 1;
    overflow: hidden;
    border-radius: 23px 0 0 23px;
  }
`;

const ShareContainer = styled.div`
  display: none;
  margin-top: 24px;
  justify-content: flex-end;

  @media ${({ theme }) => theme.bp.tabletUp} {
    display: flex;
  }
`;

const StyledModal = styled(DialogModal)`
  max-height: 90dvh !important;
  max-width: 92vw;

  @media screen and (min-width: 870px) {
    max-width: 800px;
  }
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;

  @media ${({ theme }) => theme.bp.tabletUp} {
    flex-direction: row;
  }
`;

const ModalImage = styled(Image)<{ isSubProfile?: boolean }>`
  object-fit: ${({ isSubProfile }) => (isSubProfile ? "contain" : "cover")};
`;

export const ForestModalBase = ({
  children,
  closePopup,
  countryFlag,
  isPending,
  enableSharing = true,
  imagePath,
  imageLabel,
  isEmbed,
  isOpen,
  isSubProfile,
}: {
  children: React.ReactNode;
  closePopup: () => void;
  isPending?: boolean;
  countryFlag?: JSX.Element;
  enableSharing?: boolean;
  imagePath: string;
  imageLabel?: string;
  isEmbed?: boolean;
  isOpen: boolean;
  isSubProfile?: boolean;
}) => {
  usePreventScroll(isEmbed);

  const [isImageLoaded, setIsImageLoaded] = useState(false);

  const imageAltText = isSubProfile
    ? "Welcome!"
    : "A photo of the project (or in some circumstances, the actual tree)";
  const imageDimension = isSubProfile ? 310 : undefined;

  return (
    <>
      <StyledModal
        inline
        isOpen={isOpen}
        onClose={closePopup}
        scrollable
        dataTestId="forest-modal"
      >
        <Wrapper>
          <Controls>
            <ControlsContainer>
              {enableSharing && <ShareButton />}
            </ControlsContainer>
          </Controls>

          <ImageWrapper isSubProfile={isSubProfile}>
            {!isImageLoaded && <Spinner isLarge />}

            <ModalImage
              alt={imageAltText}
              height={imageDimension}
              width={imageDimension}
              fill={!isSubProfile}
              onLoadingComplete={() => setIsImageLoaded(true)}
              src={imagePath}
            />

            <PictureAdornmentContainer>
              {countryFlag && <FlagContainer>{countryFlag}</FlagContainer>}
              {isPending && (
                <StatusTag
                  label="Pending"
                  variant="info"
                  size="large"
                  helpText="This impact is currently pending and will be applied once it is funded."
                  data-testid="forest-modal-pending"
                />
              )}
            </PictureAdornmentContainer>

            {imageLabel && <ImageLabel>{imageLabel}</ImageLabel>}
          </ImageWrapper>

          <Content>
            {children}
            {enableSharing && (
              <ShareContainer>
                <ShareButton />
              </ShareContainer>
            )}
          </Content>
        </Wrapper>
      </StyledModal>
    </>
  );
};
